import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast as notify } from "sonner";
import { mutate } from "swr";
import { Button } from "@design-system/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@design-system/components/dialog";
import { Input } from "@design-system/components/input";
import { Label } from "@design-system/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Text } from "@design-system/components/Text";
import { organizationsApi } from "@/lib/api";
import { TEAM_SWR_KEYS } from "./useTeamData";
import { SWR_KEYS as BILLING_SWR_KEYS } from "./billing/useBillingData";
import { REFERRAL_BONUS_CREDITS } from "@/components/referral/constants";

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationToken: string;
  organizationName: string;
}

export function InviteDialog({
  open,
  onOpenChange,
  organizationToken,
  organizationName,
}: InviteDialogProps) {
  const { t } = useTranslation(["app", "common"]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email.trim()) return;

    setSending(true);
    try {
      await organizationsApi.createInvite(organizationToken, email.trim(), role);
      notify.success(t('referral.inviteSent', { email }));
      mutate(TEAM_SWR_KEYS.invites(organizationToken));
      mutate(BILLING_SWR_KEYS.memberUsage(organizationToken));
      setEmail("");
      setRole("member");
      onOpenChange(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || t('settingsInvite.sendFailed');
      notify.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          setEmail("");
          setRole("member");
        }
        onOpenChange(val);
      }}
    >
      <DialogContent className="w-[540px] bg-background dark:bg-card border-border dark:border-[hsl(var(--card-inset))] rounded-[16px] p-0 gap-0 shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)] [&>button]:text-muted-foreground [&>button]:hover:text-foreground dark:[&>button]:text-white/70 dark:[&>button]:hover:text-white">
        <div className="flex flex-col items-center gap-7 p-8">
          <DialogHeader className="w-full space-y-1">
            <DialogTitle className="font-display text-2xl text-foreground dark:text-white tracking-[-0.02em]">
              Invite new team member
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Send an invitation to join <strong>{organizationName}</strong>.
              {" "}You&apos;ll both earn{" "}
              <strong className="text-foreground">{REFERRAL_BONUS_CREDITS} bonus credits</strong> when they sign up.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 w-full">
            <div className="space-y-2">
              <Label
                htmlFor="invite-email"
                className="text-sm font-medium text-foreground dark:text-white"
              >
                {t('settingsInvite.emailAddress')}
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('settingsInvite.emailPlaceholder')}
                className="rounded-[6px] border-input bg-background dark:bg-card text-foreground dark:text-white placeholder:text-muted-foreground/50 focus:border-input-focus/50 focus:ring-input-focus/20"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !sending) handleSend();
                }}
                data-testid="invite-email-input"
              />
              <Text size="xs" color="muted">
                They will receive an email with a link to register and join
                your organization.
              </Text>
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="invite-role"
                className="text-sm font-medium text-foreground dark:text-white"
              >
                Role
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger
                  className="h-10 w-full rounded-[6px] border border-input bg-background dark:bg-card px-3 text-sm text-foreground dark:text-white focus:ring-0 focus:ring-offset-0 focus:border-input-focus/50"
                  data-testid="invite-role-select"
                >
                  <SelectValue placeholder={t('settingsInvite.selectRole')} />
                </SelectTrigger>
                <SelectContent className="rounded-[6px] border border-border bg-popover dark:bg-card text-foreground dark:text-white">
                  <SelectItem
                    value="member"
                    className="rounded py-2 text-sm text-foreground dark:text-white focus:bg-accent dark:focus:bg-accent focus:text-foreground dark:focus:text-white"
                  >
                    {t('settingsInvite.member')}
                  </SelectItem>
                  <SelectItem
                    value="admin"
                    className="rounded py-2 text-sm text-foreground dark:text-white focus:bg-accent dark:focus:bg-accent focus:text-foreground dark:focus:text-white"
                  >
                    {t('settingsInvite.admin')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Text size="xs" color="muted">
                Members can view and use the workspace. Admins can also invite
                and manage team members.
              </Text>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4">
            <Button
              onClick={handleSend}
              disabled={sending || !email.trim()}
              variant="cta"
              className="h-10 rounded-[6px] px-4 text-base font-medium"
              data-testid="invite-send-button"
            >
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('sending', { ns: 'common' })}
                </>
              ) : (
                t('settingsInvite.sendInvite')
              )}
            </Button>
            <button
              type="button"
              className="flex items-center justify-center h-10 px-4 rounded-[6px] border border-foreground dark:border-input-focus text-foreground dark:text-input-focus text-base font-medium transition-colors hover:bg-foreground/10 dark:hover:bg-input-focus/10"
              onClick={() => onOpenChange(false)}
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
